import React, { useEffect, useState } from 'react';
import {
	Box,
	Card,
	CardContent,
	Typography,
	Alert,
	Button,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	TableContainer,
	Chip,
	IconButton,
	Tooltip,
	LinearProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Badge
} from '@mui/material';
import {
	Upload as UploadIcon,
	Download as DownloadIcon,
	InsertDriveFile as FileIcon,
	PictureAsPdf as PdfIcon,
	Image as ImageIcon,
	Refresh as RefreshIcon,
	ExpandMore as ExpandMoreIcon,
	Folder as FolderIcon,
	Business as BusinessIcon,
	Assignment as DocumentIcon
} from '@mui/icons-material';
import { useNotification } from '../../../contexts/NotificationContext';

interface CustomerCaseFileManagerProps {
	caseId: string;
	onFilesChanged?: () => void;
	height?: string | number;
	sections?: {
		positionFiles?: boolean;
		supplierQuoteFiles?: boolean;
		caseDocuments?: boolean;
	};
}

interface CustomerPositionFileRecord {
	id: string;
	position_id: string;
	position_number?: number;
	filename: string;          // stored random filename
	originalname: string;      // original uploaded name
	file_path?: string;        // full storage path (storage/uploads/unified/xxx)
	size: number;
	type: string;              // image, application, etc.
	mimetype: string;          // application/pdf, image/png
	description?: string;
	uploaded_at: string;
	uploaded_by?: string;
}

interface SupplierQuoteFileRecord {
	id: string;
	supplier_case_id: string;
	supplier_case_number: string;
	supplier_name: string;
	supplier_email: string;
	file_type: 'order_pdf' | 'position_file';
	filename: string;
	file_path?: string;
	originalname: string;
	description?: string;
	uploaded_at: string;
	size: number;
	mimetype: string;
	position_number?: number;
}

interface SupplierQuoteFilesGroup {
	case_id: string;
	case_number: string;
	supplier_name: string;
	supplier_email: string;
	files: SupplierQuoteFileRecord[];
}

interface CaseDocumentRecord {
	id: string;
	document_type: 'invoice' | 'quote_pdf' | 'shipping_label';
	filename: string | null;
	originalname: string;
	description: string;
	uploaded_at: string | null;
	size: number;
	mimetype: string;
	status: 'uploaded' | 'placeholder';
}

export const CustomerCaseFileManager: React.FC<CustomerCaseFileManagerProps> = ({ 
	caseId, 
	onFilesChanged, 
	height = 420,
	sections = {
		positionFiles: true,
		supplierQuoteFiles: true,
		caseDocuments: true
	}
}) => {
	const { showNotification } = useNotification();
	const [loading, setLoading] = useState(false);
	
	// Position files state
	const [filesByPosition, setFilesByPosition] = useState<Record<string, CustomerPositionFileRecord[]>>({});
	const [flatFiles, setFlatFiles] = useState<CustomerPositionFileRecord[]>([]);
	
	// Supplier quote files state
	const [supplierQuoteFiles, setSupplierQuoteFiles] = useState<Record<string, SupplierQuoteFilesGroup>>({});
	const [loadingSupplierFiles, setLoadingSupplierFiles] = useState(false);
	
	// Case documents state
	const [caseDocuments, setCaseDocuments] = useState<CaseDocumentRecord[]>([]);
	const [loadingCaseDocuments, setLoadingCaseDocuments] = useState(false);
	
	// Upload dialog state
	const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [selectedPositionId, setSelectedPositionId] = useState<string | null>(null);
	const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
	
	// Accordion expansion state
	const [expanded, setExpanded] = useState<{[key: string]: boolean}>({
		positionFiles: true,
		supplierQuoteFiles: false,
		caseDocuments: false
	});

	const loadPositionFiles = async () => {
		if (!sections.positionFiles) return;
		
		setLoading(true);
		try {
			const resp = await fetch(`/api/v3/cases/${caseId}/files/positions`);
			const data = await resp.json();
			if (data.success) {
				const grouped: Record<string, CustomerPositionFileRecord[]> = data.data || {};
				setFilesByPosition(grouped);
				const all: CustomerPositionFileRecord[] = (Object.values(grouped) as any[]).flat() as CustomerPositionFileRecord[];
				setFlatFiles(all);
			} else {
				showNotification(data.message || 'Fehler beim Laden der Positionsdateien', 'error');
			}
		} catch (e: any) {
			console.error('Error loading customer position files', e);
			showNotification(`Fehler beim Laden der Positionsdateien: ${e.message}`, 'error');
		} finally {
			setLoading(false);
		}
	};

	const loadSupplierQuoteFiles = async () => {
		if (!sections.supplierQuoteFiles) return;
		
		setLoadingSupplierFiles(true);
		try {
			const resp = await fetch(`/api/v3/cases/${caseId}/files/supplier-quotes`);
			const data = await resp.json();
			if (data.success) {
				setSupplierQuoteFiles(data.data || {});
			} else {
				showNotification(data.message || 'Fehler beim Laden der Lieferanten-Dateien', 'error');
			}
		} catch (e: any) {
			console.error('Error loading supplier quote files', e);
			showNotification(`Fehler beim Laden der Lieferanten-Dateien: ${e.message}`, 'error');
		} finally {
			setLoadingSupplierFiles(false);
		}
	};

	const loadCaseDocuments = async () => {
		if (!sections.caseDocuments) return;
		
		setLoadingCaseDocuments(true);
		try {
			const resp = await fetch(`/api/v3/cases/${caseId}/files/case-documents`);
			const data = await resp.json();
			if (data.success) {
				setCaseDocuments(data.data || []);
			} else {
				showNotification(data.message || 'Fehler beim Laden der Fall-Dokumente', 'error');
			}
		} catch (e: any) {
			console.error('Error loading case documents', e);
			showNotification(`Fehler beim Laden der Fall-Dokumente: ${e.message}`, 'error');
		} finally {
			setLoadingCaseDocuments(false);
		}
	};

	const loadAllFiles = async () => {
		await Promise.all([
			loadPositionFiles(),
			loadSupplierQuoteFiles(),
			loadCaseDocuments()
		]);
	};

	useEffect(() => {
		if (caseId) loadAllFiles();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [caseId]);

	const openUpload = (positionId?: string) => {
		setSelectedPositionId(positionId || null);
		setSelectedFiles(null);
		setUploadDialogOpen(true);
	};

	const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSelectedFiles(e.target.files);
	};

	const uploadFiles = async () => {
		if (!selectedFiles || selectedFiles.length === 0) {
			showNotification('Bitte Datei(en) auswählen', 'error');
			return;
		}
		if (!selectedPositionId) {
			showNotification('Position-ID fehlt – bitte eine Position wählen', 'error');
			return;
		}
		setUploading(true);
		try {
			const formData = new FormData();
			Array.from(selectedFiles).forEach(f => formData.append('files', f));
			const resp = await fetch(`/api/v3/cases/positions/${selectedPositionId}/files`, {
				method: 'POST',
				body: formData
			});
			const json = await resp.json();
			if (resp.ok && json.success) {
				showNotification(`${json.data.length} Datei(en) hochgeladen`, 'success');
				setUploadDialogOpen(false);
				await loadAllFiles();
				onFilesChanged?.();
			} else {
				showNotification(json.message || 'Upload fehlgeschlagen', 'error');
			}
		} catch (e: any) {
			console.error('Upload error', e);
			showNotification(`Upload fehlgeschlagen: ${e.message}`, 'error');
		} finally {
			setUploading(false);
		}
	};

	const getDownloadUrl = (file: CustomerPositionFileRecord | SupplierQuoteFileRecord | CaseDocumentRecord) => {
		// Handle different file types
		if ('position_id' in file) {
			// Customer position file
			const filenameOnly = file.filename;
			return `/api/v3/files/uploads/unified/${filenameOnly}`;
		} else if ('supplier_case_id' in file) {
			// Supplier quote file
			if (file.file_type === 'order_pdf' && file.file_path) {
				return `/api/v3/files/${file.file_path}`;
			} else {
				const filenameOnly = file.filename;
				return `/api/v3/files/uploads/unified/${filenameOnly}`;
			}
		} else {
			// Case document - placeholder for now
			return '#';
		}
	};

	const formatSize = (bytes: number) => {
		if (!bytes) return '0 B';
		const units = ['B','KB','MB','GB'];
		let i = 0; let num = bytes;
		while (num >= 1024 && i < units.length - 1) { num /= 1024; i++; }
		return `${num.toFixed(1)} ${units[i]}`;
	};

	const handleAccordionChange = (section: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
		setExpanded(prev => ({
			...prev,
			[section]: isExpanded
		}));
	};

	const getFileIcon = (file: CustomerPositionFileRecord | SupplierQuoteFileRecord | CaseDocumentRecord) => {
		const mimetype = file.mimetype;
		if (mimetype === 'application/pdf') return <PdfIcon fontSize="small" />;
		if (mimetype.startsWith('image/')) return <ImageIcon fontSize="small" />;
		return <FileIcon fontSize="small" />;
	};

	const getTotalFileCount = () => {
		const positionCount = flatFiles.length;
		const supplierCount = Object.values(supplierQuoteFiles).reduce((sum, group) => sum + group.files.length, 0);
		const documentCount = caseDocuments.filter(doc => doc.status === 'uploaded').length;
		return positionCount + supplierCount + documentCount;
	};

	const grouped = Object.entries(filesByPosition).sort((a,b) => {
		const pa = a[1][0]?.position_number || 0;
		const pb = b[1][0]?.position_number || 0;
		return pa - pb;
	});

	const anyLoading = loading || loadingSupplierFiles || loadingCaseDocuments;

	return (
		<Box>
			{/* Header Card */}
			<Card sx={{ mb: 2 }}>
				<CardContent>
					<Box display="flex" justifyContent="space-between" alignItems="center">
						<Typography variant="h6">
							Dateiverwaltung ({getTotalFileCount()})
						</Typography>
						<Box display="flex" gap={1}>
							<Tooltip title="Neu laden">
								<IconButton onClick={loadAllFiles} disabled={anyLoading}>
									<RefreshIcon />
								</IconButton>
							</Tooltip>
						</Box>
					</Box>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
						Alle Dateien für diesen Kundenfall: Positionsdateien, Lieferanten-Angebote und Fall-Dokumente.
					</Typography>
				</CardContent>
			</Card>

			{anyLoading && <LinearProgress sx={{ mb: 2 }} />}

			{/* Position Files Section */}
			{sections.positionFiles && (
				<Accordion 
					expanded={expanded.positionFiles} 
					onChange={handleAccordionChange('positionFiles')}
					sx={{ mb: 2 }}
				>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Box display="flex" alignItems="center" gap={1}>
							<FolderIcon color="primary" />
							<Typography variant="h6">
								Kunden-Positionsdateien
							</Typography>
							<Badge badgeContent={flatFiles.length} color="primary" sx={{ ml: 1 }} />
						</Box>
					</AccordionSummary>
					<AccordionDetails>
						{loading ? (
							<LinearProgress sx={{ mb: 2 }} />
						) : flatFiles.length === 0 ? (
							<Alert severity="info">
								Es wurden noch keine Positionsdateien hochgeladen. Wählen Sie eine Position aus, um Dateien hinzuzufügen.
							</Alert>
						) : (
							<Box>
								{grouped.map(([positionId, files]) => (
									<Card key={positionId} sx={{ mb: 2 }} variant="outlined">
										<CardContent>
											<Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
												<Typography variant="subtitle1" fontWeight={600}>
													Position {files[0]?.position_number || '?'} – {files.length} Datei(en)
												</Typography>
												<Button
													size="small"
													startIcon={<UploadIcon />}
													variant="outlined"
													onClick={() => openUpload(positionId)}
												>
													Datei hochladen
												</Button>
											</Box>
											<TableContainer>
												<Table size="small">
													<TableHead>
														<TableRow>
															<TableCell>Datei</TableCell>
															<TableCell>Typ</TableCell>
															<TableCell>Größe</TableCell>
															<TableCell>Hochgeladen</TableCell>
															<TableCell align="center">Aktionen</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{files.map(f => (
															<TableRow key={f.id} hover>
																<TableCell>
																	<Box display="flex" alignItems="center" gap={1}>
																		{getFileIcon(f)}
																		<Typography variant="body2">{f.originalname}</Typography>
																	</Box>
																</TableCell>
																<TableCell>
																	<Chip size="small" label={f.type || f.mimetype.split('/')[0]} />
																</TableCell>
																<TableCell>
																	<Typography variant="caption">{formatSize(f.size)}</Typography>
																</TableCell>
																<TableCell>
																	<Typography variant="caption">{new Date(f.uploaded_at).toLocaleDateString('de-DE')}</Typography>
																</TableCell>
																<TableCell align="center">
																	<Tooltip title="Herunterladen">
																		<IconButton size="small" onClick={() => window.open(getDownloadUrl(f), '_blank')}>
																			<DownloadIcon fontSize="small" />
																		</IconButton>
																	</Tooltip>
																</TableCell>
															</TableRow>
														))}
													</TableBody>
												</Table>
											</TableContainer>
										</CardContent>
									</Card>
								))}
							</Box>
						)}
					</AccordionDetails>
				</Accordion>
			)}

			{/* Supplier Quote Files Section */}
			{sections.supplierQuoteFiles && (
				<Accordion 
					expanded={expanded.supplierQuoteFiles} 
					onChange={handleAccordionChange('supplierQuoteFiles')}
					sx={{ mb: 2 }}
				>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Box display="flex" alignItems="center" gap={1}>
							<BusinessIcon color="secondary" />
							<Typography variant="h6">
								Lieferanten-Angebotsdateien
							</Typography>
							<Badge 
								badgeContent={Object.values(supplierQuoteFiles).reduce((sum, group) => sum + group.files.length, 0)} 
								color="secondary" 
								sx={{ ml: 1 }} 
							/>
						</Box>
					</AccordionSummary>
					<AccordionDetails>
						{loadingSupplierFiles ? (
							<LinearProgress sx={{ mb: 2 }} />
						) : Object.keys(supplierQuoteFiles).length === 0 ? (
							<Alert severity="info">
								Noch keine Lieferanten-Angebotsdateien verfügbar. Diese werden automatisch angezeigt, wenn Lieferanten-Angebote erstellt und konvertiert werden.
							</Alert>
						) : (
							<Box>
								{Object.values(supplierQuoteFiles).map((supplierGroup) => (
									<Card key={supplierGroup.case_id} sx={{ mb: 2 }} variant="outlined">
										<CardContent>
											<Typography variant="subtitle1" fontWeight={600} gutterBottom>
												{supplierGroup.supplier_name} – {supplierGroup.case_number}
											</Typography>
											<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
												{supplierGroup.supplier_email} • {supplierGroup.files.length} Datei(en)
											</Typography>
											<TableContainer>
												<Table size="small">
													<TableHead>
														<TableRow>
															<TableCell>Datei</TableCell>
															<TableCell>Typ</TableCell>
															<TableCell>Position</TableCell>
															<TableCell>Größe</TableCell>
															<TableCell align="center">Aktionen</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{supplierGroup.files.map(f => (
															<TableRow key={f.id} hover>
																<TableCell>
																	<Box display="flex" alignItems="center" gap={1}>
																		{getFileIcon(f)}
																		<Typography variant="body2">{f.originalname}</Typography>
																	</Box>
																</TableCell>
																<TableCell>
																	<Chip 
																		size="small" 
																		label={f.file_type === 'order_pdf' ? 'Bestell-PDF' : 'Position'} 
																		color={f.file_type === 'order_pdf' ? 'primary' : 'default'}
																	/>
																</TableCell>
																<TableCell>
																	<Typography variant="caption">
																		{f.position_number ? `Pos. ${f.position_number}` : '-'}
																	</Typography>
																</TableCell>
																<TableCell>
																	<Typography variant="caption">{formatSize(f.size)}</Typography>
																</TableCell>
																<TableCell align="center">
																	<Tooltip title="Herunterladen">
																		<IconButton size="small" onClick={() => window.open(getDownloadUrl(f), '_blank')}>
																			<DownloadIcon fontSize="small" />
																		</IconButton>
																	</Tooltip>
																</TableCell>
															</TableRow>
														))}
													</TableBody>
												</Table>
											</TableContainer>
										</CardContent>
									</Card>
								))}
							</Box>
						)}
					</AccordionDetails>
				</Accordion>
			)}

			{/* Case Documents Section */}
			{sections.caseDocuments && (
				<Accordion 
					expanded={expanded.caseDocuments} 
					onChange={handleAccordionChange('caseDocuments')}
					sx={{ mb: 2 }}
				>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Box display="flex" alignItems="center" gap={1}>
							<DocumentIcon color="warning" />
							<Typography variant="h6">
								Fall-Dokumente
							</Typography>
							<Badge 
								badgeContent={caseDocuments.filter(doc => doc.status === 'uploaded').length} 
								color="warning" 
								sx={{ ml: 1 }} 
							/>
						</Box>
					</AccordionSummary>
					<AccordionDetails>
						{loadingCaseDocuments ? (
							<LinearProgress sx={{ mb: 2 }} />
						) : (
							<Box>
								<Alert severity="info" sx={{ mb: 2 }}>
									<strong>Zukünftige Funktionalität:</strong> Fall-spezifische Dokumente wie Rechnungen, finale Angebots-PDFs und Versandetiketten werden hier verwaltet.
								</Alert>
								<TableContainer>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell>Dokument</TableCell>
												<TableCell>Status</TableCell>
												<TableCell>Beschreibung</TableCell>
												<TableCell align="center">Aktionen</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{caseDocuments.map(doc => (
												<TableRow key={doc.id} hover>
													<TableCell>
														<Box display="flex" alignItems="center" gap={1}>
															{getFileIcon(doc)}
															<Typography variant="body2">{doc.originalname}</Typography>
														</Box>
													</TableCell>
													<TableCell>
														<Chip 
															size="small" 
															label={doc.status === 'placeholder' ? 'Zukünftig' : 'Verfügbar'} 
															color={doc.status === 'placeholder' ? 'default' : 'success'}
														/>
													</TableCell>
													<TableCell>
														<Typography variant="caption" color="text.secondary">
															{doc.description}
														</Typography>
													</TableCell>
													<TableCell align="center">
														{doc.status === 'uploaded' ? (
															<Tooltip title="Herunterladen">
																<IconButton size="small" onClick={() => window.open(getDownloadUrl(doc), '_blank')}>
																	<DownloadIcon fontSize="small" />
																</IconButton>
															</Tooltip>
														) : (
															<Typography variant="caption" color="text.disabled">
																Noch nicht verfügbar
															</Typography>
														)}
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</Box>
						)}
					</AccordionDetails>
				</Accordion>
			)}

			{/* Upload dialog - same as before */}
			<Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Datei(en) hochladen</DialogTitle>
				<DialogContent>
					{!selectedPositionId && (
						<Alert severity="warning" sx={{ mb: 2 }}>
							Keine Position referenziert – dieser Dialog wurde ohne Positions-ID geöffnet.
						</Alert>
					)}
					<Box
						sx={{
							border: '2px dashed #ccc',
							borderRadius: 2,
							p: 3,
							textAlign: 'center',
							cursor: 'pointer',
							'&:hover': {
								borderColor: 'primary.main',
								backgroundColor: 'rgba(25, 118, 210, 0.04)'
							}
						}}
						onClick={() => document.getElementById('customer-position-file-input')?.click()}
					>
						<input
							id="customer-position-file-input"
							type="file"
							hidden
							multiple
							onChange={handleFileInputChange}
							accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt"
						/>
						<UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
						<Typography variant="body1" gutterBottom>
							{selectedFiles && selectedFiles.length > 0
								? `${selectedFiles.length} Datei(en) ausgewählt`
								: 'Klicken Sie hier, um Dateien auszuwählen'}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Unterstützte Formate: PDF, DOC, DOCX, JPG, PNG, GIF, TXT
						</Typography>
					</Box>
					{uploading && (
						<Box sx={{ mt: 2 }}>
							<LinearProgress />
							<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
								Upload läuft...
							</Typography>
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>Abbrechen</Button>
					<Button onClick={uploadFiles} variant="contained" disabled={!selectedFiles || uploading}>Hochladen</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default CustomerCaseFileManager;